import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Publish as PublishIcon,
  Replay as ReplayIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import React, { useState } from 'react';
import { ReviewStatus } from '../../models/ReviewRequest';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import { StyledCard, StyledCardContent, StyledTypography } from '../styles/styles';

interface ReviewStatusActionsProps {
  currentStatus: ReviewStatus;
  onStatusUpdate: (status: ReviewStatus) => void;
  currentUser?: { id: string; name: string };
  requestedBy?: { id: string; name: string };
  fundOpportunityId?: string;
  onPublish?: () => void;
  tone?: keyof typeof Palette;
  reviewData?: any; // Review data to check for changes
}

export default function ReviewStatusActions({
  currentStatus,
  onStatusUpdate,
  currentUser,
  requestedBy,
  fundOpportunityId,
  onPublish,
  tone = 'tone1',
  reviewData,
}: ReviewStatusActionsProps) {
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<ReviewStatus | null>(null);
  const [statusComment, setStatusComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const handleStatusClick = (status: ReviewStatus) => {
    setSelectedStatus(status);
    setShowStatusDialog(true);
  };

  const handleResubmit = () => {
    // Resubmit changes the status back to PENDING
    setSelectedStatus('REVIEW_CHANGES');
    setShowStatusDialog(true);
  };

  const handleStatusSubmit = async () => {
    if (!selectedStatus) return;

    // Require comments for approve and reject actions
    const requiresComment =
      selectedStatus === 'REVIEW_COMPLETE' || selectedStatus === 'REVIEW_REJECTED';
    if (requiresComment && !statusComment.trim()) {
      return; // Don't submit if comment is required but not provided
    }

    setSubmitting(true);
    try {
      // Submit the status comment if provided
      if (statusComment.trim()) {
        // This would be handled by the parent component
        // For now, we'll just update the status
      }

      await onStatusUpdate(selectedStatus);
      setShowStatusDialog(false);
      setStatusComment('');
      setSelectedStatus(null);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowStatusDialog(false);
    setStatusComment('');
    setSelectedStatus(null);
  };

  const handlePublishClick = () => {
    setShowPublishDialog(true);
  };

  const handlePublishConfirm = async () => {
    if (!onPublish) return;

    setPublishing(true);
    try {
      await onPublish();
      setShowPublishDialog(false);
    } catch (error) {
      console.error('Error publishing content:', error);
    } finally {
      setPublishing(false);
    }
  };

  const handlePublishCancel = () => {
    setShowPublishDialog(false);
  };

  const getStatusButtonProps = (status: ReviewStatus) => {
    switch (status) {
      case 'REVIEW_COMPLETE':
        return {
          color: 'success' as const,
          startIcon: <CheckCircleIcon />,
          label: 'Approve Review',
        };
      case 'CHANGES_REQUESTED':
        return {
          color: 'warning' as const,
          startIcon: <ReplayIcon />,
          label: 'Request Changes',
        };
      case 'REVIEW_REJECTED':
        return {
          color: 'error' as const,
          startIcon: <CloseIcon />,
          label: 'Reject Review',
        };
      default:
        return {
          color: 'primary' as const,
          startIcon: null,
          label: status.replace('_', ' '),
        };
    }
  };

  const getStatusDescription = (status: ReviewStatus) => {
    switch (status) {
      case 'REVIEW_COMPLETE':
        return 'Mark this review as complete. The content can now be published.';
      case 'CHANGES_REQUESTED':
        return 'Request changes from the content creator. They will need to address your feedback.';
      case 'REVIEW_REJECTED':
        return 'Reject this review. The content will not be approved for publication.';
      default:
        return '';
    }
  };

  const canUpdateStatus =
    currentStatus === 'PENDING' ||
    currentStatus === 'CHANGES_REQUESTED' ||
    currentStatus === 'REVIEW_CHANGES';
  // Check if current user is the one who requested the review
  const isCurrentUserRequester = currentUser && requestedBy && currentUser.id === requestedBy.id;

  // Check if there are any changes requested (even if not submitted back)
  const hasChangesRequested =
    reviewData?.changes?.some((change: any) => change.changed && !change.accepted) || false;

  // User who requested the review cannot approve it, but can request changes
  // Approval is only allowed from PENDING or REVIEW_CHANGES status
  // Cannot approve if there are changes requested
  const canApprove =
    !isCurrentUserRequester &&
    (currentStatus === 'PENDING' || currentStatus === 'REVIEW_CHANGES') &&
    !hasChangesRequested;

  // When status is CHANGES_REQUESTED, only the requester can request changes or resubmit
  // When status is PENDING, both can request changes
  const canRequestChanges = currentStatus === 'PENDING';

  // Only show reject option when status is PENDING and user is the reviewer
  const canReject = currentStatus === 'PENDING' && !isCurrentUserRequester;

  // Show resubmit option when status is CHANGES_REQUESTED and user is the requester
  const canResubmit = currentStatus === 'CHANGES_REQUESTED' && isCurrentUserRequester;

  if (!canUpdateStatus) {
    // Show publish button if status is REVIEW_COMPLETE
    if (currentStatus === 'REVIEW_COMPLETE' && onPublish && fundOpportunityId) {
      return (
        <>
          <StyledCard tone={tone}>
            <StyledCardContent>
              <StyledTypography tone={tone} variant="h6" sx={{ mb: 2 }}>
                Review Complete - Ready to Publish
              </StyledTypography>
              <Alert severity="success" sx={{ mb: 2 }}>
                This review has been completed and approved. The content is ready to be published.
              </Alert>
              <StyledButton
                variant="contained"
                color="success"
                startIcon={<PublishIcon />}
                onClick={handlePublishClick}
              >
                Publish Content
              </StyledButton>
            </StyledCardContent>
          </StyledCard>

          {/* Publish Confirmation Dialog */}
          <Dialog open={showPublishDialog} onClose={handlePublishCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Publish Content</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <StyledTypography tone={tone} variant="body2" color="text.secondary">
                  Are you sure you want to publish this content? This action will make the content
                  live and visible to users.
                </StyledTypography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handlePublishCancel} disabled={publishing}>
                Cancel
              </Button>
              <StyledButton
                onClick={handlePublishConfirm}
                disabled={publishing}
                variant="contained"
                color="success"
                startIcon={publishing ? <CircularProgress size={20} /> : <PublishIcon />}
              >
                {publishing ? 'Publishing...' : 'Publish Content'}
              </StyledButton>
            </DialogActions>
          </Dialog>
        </>
      );
    }

    return (
      <StyledCard tone={tone}>
        <StyledCardContent>
          <Alert severity="info">This review has been completed and cannot be modified.</Alert>
        </StyledCardContent>
      </StyledCard>
    );
  }

  return (
    <>
      <StyledCard tone={tone}>
        <StyledCardContent>
          <StyledTypography tone={tone} variant="h6" sx={{ mb: 2 }}>
            Review Actions
          </StyledTypography>

          <Stack direction="row" spacing={2} flexWrap="wrap">
            {canApprove && (
              <StyledButton
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleStatusClick('REVIEW_COMPLETE')}
              >
                Approve Review
              </StyledButton>
            )}

            {canRequestChanges && (
              <StyledButton
                variant="contained"
                color="warning"
                startIcon={<ReplayIcon />}
                onClick={() => handleStatusClick('CHANGES_REQUESTED')}
              >
                Request Changes
              </StyledButton>
            )}

            {canReject && (
              <StyledButton
                variant="contained"
                color="error"
                startIcon={<CloseIcon />}
                onClick={() => handleStatusClick('REVIEW_REJECTED')}
              >
                Reject Review
              </StyledButton>
            )}

            {canResubmit && (
              <StyledButton
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleResubmit}
              >
                Resubmit for Review
              </StyledButton>
            )}
          </Stack>
        </StyledCardContent>
      </StyledCard>

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedStatus === 'PENDING' && currentStatus === 'CHANGES_REQUESTED'
            ? 'Resubmit for Review'
            : selectedStatus && getStatusButtonProps(selectedStatus).label}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <StyledTypography tone={tone} variant="body2" color="text.secondary">
              {selectedStatus === 'PENDING' && currentStatus === 'CHANGES_REQUESTED'
                ? 'Resubmit this review for the reviewer to take another look at your changes.'
                : selectedStatus && getStatusDescription(selectedStatus)}
            </StyledTypography>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            label={
              selectedStatus === 'REVIEW_COMPLETE' || selectedStatus === 'REVIEW_REJECTED'
                ? 'Add a comment (required)'
                : 'Add a comment (optional)'
            }
            placeholder={
              selectedStatus === 'REVIEW_COMPLETE'
                ? "Explain why you're approving this review..."
                : selectedStatus === 'REVIEW_REJECTED'
                  ? "Explain why you're rejecting this review..."
                  : selectedStatus === 'PENDING' && currentStatus === 'CHANGES_REQUESTED'
                    ? "Describe the changes you've made..."
                    : 'Explain your decision or provide feedback...'
            }
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            required={selectedStatus === 'REVIEW_COMPLETE' || selectedStatus === 'REVIEW_REJECTED'}
            error={
              (selectedStatus === 'REVIEW_COMPLETE' || selectedStatus === 'REVIEW_REJECTED') &&
              !statusComment.trim()
            }
            helperText={
              (selectedStatus === 'REVIEW_COMPLETE' || selectedStatus === 'REVIEW_REJECTED') &&
              !statusComment.trim()
                ? 'A comment is required for this action'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={submitting}>
            Cancel
          </Button>
          <StyledButton
            onClick={handleStatusSubmit}
            disabled={
              submitting ||
              ((selectedStatus === 'REVIEW_COMPLETE' || selectedStatus === 'REVIEW_REJECTED') &&
                !statusComment.trim())
            }
            variant="contained"
            color={selectedStatus ? getStatusButtonProps(selectedStatus).color : 'primary'}
          >
            {submitting ? 'Updating...' : 'Update Status'}
          </StyledButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
